import { createClient } from '@/lib/supabase/client';

export type AuditActionType =
  | 'user_login' |'user_logout' |'duty_assigned' |'duty_updated' |'duty_deleted' |'crew_created' |'crew_updated' |'crew_deleted' |'role_changed' |'warning_dismissed' |'leave_approved' |'leave_rejected' |'violation_recorded' |'reward_granted' |'settings_changed' |'user_created' |'user_deleted';

export interface AuditLogEntry {
  action: AuditActionType;
  actor_id?: string | null;
  actor_email?: string | null;
  actor_name?: string | null;
  target_type?: string | null;
  target_id?: string | null;
  target_name?: string | null;
  change_details?: Record<string, unknown> | null;
}

// ─── Cached actor info ────────────────────────────────────────────────────────
// Cache the current user for 10 minutes to avoid a getUser() round-trip on
// every single create/update/delete operation across the app.
let _cachedActor: { id: string; email: string | null; name: string | null } | null = null;
let _actorCachedAt = 0;
const ACTOR_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

async function getActor() {
  const now = Date.now();
  if (_cachedActor && now - _actorCachedAt < ACTOR_CACHE_TTL) {
    return _cachedActor;
  }
  try {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      _cachedActor = {
        id: user.id,
        email: user.email ?? null,
        name:
          user.user_metadata?.full_name ??
          user.user_metadata?.name ??
          user.email ??
          null,
      };
      _actorCachedAt = now;
      return _cachedActor;
    }
  } catch {
    // ignore — audit log should never block the main operation
  }
  return null;
}

/** Invalidate the actor cache (call on login/logout). */
export function invalidateActorCache() {
  _cachedActor = null;
  _actorCachedAt = 0;
}

// ─── Audit queue ──────────────────────────────────────────────────────────────
// Fire-and-forget queue: audit inserts are batched and sent asynchronously
// so they never add latency to the user-facing operation.
const _auditQueue: Array<Record<string, unknown>> = [];
let _auditFlushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
  if (_auditFlushTimer !== null) return;
  _auditFlushTimer = setTimeout(async () => {
    _auditFlushTimer = null;
    if (_auditQueue.length === 0) return;
    const batch = _auditQueue.splice(0, _auditQueue.length);
    try {
      const supabase = createClient();
      const { error } = await supabase.from('audit_logs').insert(batch);
      if (error) console.warn('auditLogService: batch insert failed:', error.message);
    } catch (err) {
      console.warn('auditLogService: unexpected batch error:', err);
    }
  }, 500); // flush after 500 ms — invisible to user, batches rapid writes
}

/**
 * Records a single audit log entry into Supabase.
 * Silently fails — never throws — so it never breaks the calling operation.
 * Uses a cached actor + async queue so it adds zero latency to writes.
 */
export async function recordAudit(entry: AuditLogEntry): Promise<void> {
  try {
    let actorId = entry.actor_id ?? null;
    let actorEmail = entry.actor_email ?? null;
    let actorName = entry.actor_name ?? null;

    if (!actorId || !actorEmail) {
      const actor = await getActor();
      if (actor) {
        actorId = actorId ?? actor.id;
        actorEmail = actorEmail ?? actor.email;
        actorName = actorName ?? actor.name;
      }
    }

    _auditQueue.push({
      action: entry.action,
      actor_id: actorId,
      actor_email: actorEmail,
      actor_name: actorName,
      target_type: entry.target_type ?? null,
      target_id: entry.target_id ?? null,
      target_name: entry.target_name ?? null,
      change_details: entry.change_details ?? null,
    });

    scheduleFlush();
  } catch (err) {
    console.warn('auditLogService: unexpected error:', err);
  }
}
